// All customer-facing event builder data + shared estimate logic

export const EVENT_TYPES = [
  "Birthday", "Wedding", "Baby Shower", "Bridal Shower", "Graduation",
  "Anniversary", "Corporate Event", "School Event", "Holiday Party", "Concert",
  "Family Event", "Community Event", "Fundraiser", "Bachelor/Bachelorette",
  "Quinceañera", "Retirement", "Celebration of Life", "House Party",
  "Restaurant/Bar Event", "Other",
];

export const LOCATION_OPTIONS = [
  "I already have a venue", "My home", "Restaurant/bar", "Hotel",
  "Outdoor location", "School", "Church", "Business/corporate location",
  "I need Nick to find a venue", "I don't know yet",
];

export const GUEST_OPTIONS = [
  "1–10", "11–25", "26–50", "51–100", "101–250", "251–500", "500+", "I don't know yet",
];

export const BUDGET_OPTIONS = [
  "Under $500", "$500–$1,000", "$1,000–$2,500", "$2,500–$5,000",
  "$5,000–$10,000", "$10,000+", "I'm not sure",
];

export const CATEGORIES = [
  { key: "VENUE", icon: "MapPin", items: ["Venue", "Restaurant", "Bar", "Hotel", "Outdoor", "Park", "Private space", "School", "Church", "Corporate space"] },
  { key: "FOOD & DRINK", icon: "UtensilsCrossed", items: ["Catering", "Food truck", "BBQ", "Buffet", "Restaurant", "Cake", "Desserts", "Appetizers", "Bartender", "Coffee", "Breakfast", "Brunch"] },
  { key: "MUSIC", icon: "Music", items: ["DJ", "Karaoke", "Live band", "Solo musician", "Singer", "Guitarist", "Playlist", "Sound system", "Lighting"] },
  { key: "HOSTING & ENTERTAINMENT", icon: "Mic", items: ["Nick as host", "Other host", "MC", "Bingo", "Lotería", "Trivia", "Comedy", "Magician", "Actors", "Character performers", "Dancers", "Karaoke competition", "Games", "Kids entertainment"] },
  { key: "DECOR", icon: "Sparkles", items: ["Balloons", "Flowers", "Backdrops", "Centerpieces", "Tablescapes", "Lighting", "Theme decorations", "Photo area"] },
  { key: "ACTIVITIES", icon: "PartyPopper", items: ["Piñata", "Bounce house", "Yard games", "Photo booth", "360 booth", "Face painting", "Arts and crafts", "Casino-style games"] },
  { key: "MEDIA", icon: "Camera", items: ["Photographer", "Videographer", "Photo booth", "360 booth", "Social media content", "Drone"] },
  { key: "LOGISTICS", icon: "Truck", items: ["Tables", "Chairs", "Equipment rental", "Delivery", "Setup", "Cleanup", "Security", "Transportation", "Staffing"] },
  { key: "TRAVEL", icon: "Plane", items: ["Hotels", "Hotel blocks", "Guest transportation", "Airport transportation"] },
];

export const PLANNING_PACKAGES = [
  {
    id: "EVENT STARTER",
    price: 149,
    tagline: "Basic planning and vendor recommendations.",
    includes: [
      "A friendly planning call with Nick to map out your vision",
      "A custom shortlist of trusted vendors for what you need",
      "A simple, realistic budget breakdown",
      "Nick's tips so you can confidently book it yourself",
    ],
  },
  {
    id: "EVENT PLAN",
    price: 299,
    tagline: "Vendor research, coordination, timeline and planning support.",
    includes: [
      "Everything in Event Starter",
      "Nick researches and reaches out to vendors for you",
      "A full event timeline and checklist built by Nick",
      "Ongoing planning support by text and email",
      "Help coordinating all the moving pieces",
    ],
  },
  {
    id: "NICK'S FULL SERVICE",
    price: 599,
    tagline: "Full planning and coordination plus Nick hosting up to 4 hours.",
    includes: [
      "Everything in Event Plan",
      "Nick handles booking and coordination start to finish",
      "Nick hosts your event in person — up to 4 hours",
      "Day-of setup and run-of-show so nothing slips",
      "You just show up and enjoy your party",
    ],
  },
];

// Nick charges less for smaller parties. Fees scale with the customer's budget bracket.
const PACKAGE_PRICE_BY_BUDGET = {
  "Under $500": { "EVENT STARTER": 49, "EVENT PLAN": 99, "NICK'S FULL SERVICE": 199 },
  "$500–$1,000": { "EVENT STARTER": 79, "EVENT PLAN": 149, "NICK'S FULL SERVICE": 279 },
  "$1,000–$2,500": { "EVENT STARTER": 129, "EVENT PLAN": 229, "NICK'S FULL SERVICE": 399 },
};

export function getPackagePrice(pkgId, budgetRange) {
  const tbl = PACKAGE_PRICE_BY_BUDGET[budgetRange];
  if (tbl && tbl[pkgId] != null) return tbl[pkgId];
  return PLANNING_PACKAGES.find((p) => p.id === pkgId)?.price ?? 0;
}

export function isScaledBudget(budgetRange) {
  return !!PACKAGE_PRICE_BY_BUDGET[budgetRange];
}

export const SERVICE_AREA = [
  "Houston", "Kingwood", "Atascocita", "Humble", "North Houston", "Spring", "The Woodlands", "Conroe",
];

export const CONTACT_METHODS = ["Phone call", "Text message", "Email"];

const GUEST_MID = {
  "1–10": 8, "11–25": 18, "26–50": 38, "51–100": 75,
  "101–250": 175, "251–500": 375, "500+": 600,
};

// Transparent heuristic estimate. Labeled as estimate everywhere in the UI.
export function computeEstimate({ guest_count, services, nick_decide }) {
  const guests = GUEST_MID[guest_count];
  const serviceCount =
    Object.values(services || {}).reduce((a, arr) => a + (arr?.length || 0), 0) +
    (nick_decide?.length || 0);
  if (!guests || serviceCount === 0) {
    return { has_estimate: false };
  }
  const factor = 1 + serviceCount * 0.08;
  const round = (n) => Math.round(n / 50) * 50;
  return {
    has_estimate: true,
    budget: round(guests * 20 * factor),
    recommended: round(guests * 34 * factor),
    premium: round(guests * 58 * factor),
  };
}

export function formatMoney(v) {
  if (v == null) return "—";
  return "$" + Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 });
}
